/**
 * Interactive List - List items that change associated images/text
 * Supports underline animation and smooth image/text transitions
 */

(function() {
  'use strict';

  class InteractiveList {
    constructor(container) {
      // Prevent multiple initializations on the same container
      if (container._interactiveListInstance) {
        container._interactiveListInstance.destroy();
      }
      
      this.container = container;
      this.list = container.querySelector('.interactive-list');
      this.listItems = container.querySelectorAll('.interactive-list-item');
      this.imageWrapper = container.querySelector('.interactive-list-image');
      this.imageMain = container.querySelector('.interactive-list-image-main');
      this.textOverlay = container.querySelector('.interactive-list-text-overlay');
      
      if (!this.list || !this.listItems.length) return;
      
      // Store instance on container to prevent duplicates
      container._interactiveListInstance = this;
      
      this.currentIndex = 0;
      this.slideTimeout = null;
      this.isAnimating = false;
      this.slideDuration = 3000; // 3 seconds per slide (matches underline animation)
      this.isPaused = false;
      this.imageTransitionTimeouts = [];
      this.overlayTransitionTimeouts = [];
      this.lastActivationTime = 0;
      this.stuckCheckInterval = null;
      
      this.init();
    }
    
    destroy() {
      this.stopAutoPlay();
      this.stopStuckCheck();
      if (this.slideTimeout) {
        clearTimeout(this.slideTimeout);
        this.slideTimeout = null;
      }
      this.imageTransitionTimeouts.forEach(timeout => clearTimeout(timeout));
      this.overlayTransitionTimeouts.forEach(timeout => clearTimeout(timeout));
      this.imageTransitionTimeouts = [];
      this.overlayTransitionTimeouts = [];
      this.isAnimating = false;
    }

    init() {
      this.attachEventListeners();
      this.activateItem(0, true);
      this.startStuckCheck();
    }
    
    startStuckCheck() {
      // Safety mechanism: check every 5 seconds if we're stuck
      this.stuckCheckInterval = setInterval(() => {
        const timeSinceLastActivation = Date.now() - this.lastActivationTime;
        // If it's been more than slideDuration + 1 second and we're not paused, something's wrong
        if (!this.isPaused && timeSinceLastActivation > (this.slideDuration + 1000) && this.slideTimeout === null) {
          // Restart from current item
          this.activateItem(this.currentIndex, true);
        }
      }, 5000);
    }
    
    stopStuckCheck() {
      if (this.stuckCheckInterval) {
        clearInterval(this.stuckCheckInterval);
        this.stuckCheckInterval = null;
      }
    }

    attachEventListeners() {
      this.listItems.forEach((item, index) => {
        item.addEventListener('click', () => {
          // Stop current slide timer
          this.stopAutoPlay();
          // Activate clicked item and continue slideshow from there
          this.activateItem(index, true);
        });
      });

      // Pause autoplay on hover
      if (this.container) {
        this.container.addEventListener('mouseenter', () => {
          this.pauseAutoPlay();
        });
        
        this.container.addEventListener('mouseleave', () => {
          this.resumeAutoPlay();
        });
      }
    }

    startAutoPlay() {
      // Clear any existing timeout
      this.stopAutoPlay();
      
      // Only start if not paused
      if (!this.isPaused) {
        // Schedule next slide after the underline animation completes
        // The timeout duration must match the CSS transition duration exactly
        this.slideTimeout = setTimeout(() => {
          if (!this.isPaused && this.currentIndex !== undefined && this.listItems.length > 0) {
            this.nextItem();
          }
        }, this.slideDuration);
      }
    }

    stopAutoPlay() {
      if (this.slideTimeout) {
        clearTimeout(this.slideTimeout);
        this.slideTimeout = null;
      }
    }

    pauseAutoPlay() {
      this.isPaused = true;
      this.stopAutoPlay();
    }

    resumeAutoPlay() {
      this.isPaused = false;
      // Resume from current item
      this.startAutoPlay();
    }

    nextItem() {
      // Safety check
      if (this.listItems.length === 0) return;
      
      const nextIndex = (this.currentIndex + 1) % this.listItems.length;
      // Use requestAnimationFrame to ensure smooth transition
      requestAnimationFrame(() => {
        this.activateItem(nextIndex, true);
      });
    }

    activateItem(index, startTimer = false) {
      // Validate index
      if (index < 0 || index >= this.listItems.length) {
        return;
      }
      
      // Stop any pending slide timer
      this.stopAutoPlay();
      
      // Allow re-activating the same item to restart animation
      const isSameItem = index === this.currentIndex;
      
      // Clear any pending timeouts
      this.imageTransitionTimeouts.forEach(timeout => clearTimeout(timeout));
      this.overlayTransitionTimeouts.forEach(timeout => clearTimeout(timeout));
      this.imageTransitionTimeouts = [];
      this.overlayTransitionTimeouts = [];
      
      this.isAnimating = true;

      const item = this.listItems[index];
      if (!item) return; // Safety check
      
      const itemId = item.dataset.itemId;

      // Remove active from all items and reset underlines
      this.listItems.forEach((listItem, listIndex) => {
        listItem.classList.remove('active');
        const underline = listItem.querySelector('.interactive-list-underline');
        if (underline) {
          // Only reset if it's not the item we're about to activate
          if (listIndex !== index) {
            underline.style.transition = 'none';
            underline.style.width = '0';
            // Force reflow to ensure reset is applied
            underline.offsetWidth;
          }
        }
      });

      // Activate current item
      item.classList.add('active');

      // Update image (skip on same item to avoid flicker)
      if (!isSameItem && this.imageWrapper) {
        const allImages = this.imageWrapper.querySelectorAll('.interactive-list-image-main');
        const targetImage = Array.from(allImages).find(img => img.dataset.itemId === itemId);
        
        if (targetImage) {
          // Fade out current image
          allImages.forEach(img => {
            if (!img.classList.contains('hidden')) {
              img.style.opacity = '0';
              const timeout1 = setTimeout(() => {
                img.classList.add('hidden');
              }, 250);
              this.imageTransitionTimeouts.push(timeout1);
            }
          });

          // Fade in target image
          const timeout2 = setTimeout(() => {
            targetImage.classList.remove('hidden');
            targetImage.style.opacity = '0';
            // Force reflow
            targetImage.offsetHeight;
            const timeout3 = setTimeout(() => {
              targetImage.style.opacity = '1';
            }, 10);
            this.imageTransitionTimeouts.push(timeout3);
          }, 250);
          this.imageTransitionTimeouts.push(timeout2);
        }
      }

      // Update text overlay (skip on same item to avoid flicker)
      if (!isSameItem && this.textOverlay) {
        const allOverlays = this.imageWrapper.querySelectorAll('.interactive-list-text-overlay');
        const targetOverlay = Array.from(allOverlays).find(overlay => overlay.dataset.itemId === itemId);
        
        if (targetOverlay) {
          // Fade out current overlay
          allOverlays.forEach(overlay => {
            if (!overlay.classList.contains('hidden')) {
              overlay.style.opacity = '0';
              const timeout1 = setTimeout(() => {
                overlay.classList.add('hidden');
              }, 250);
              this.overlayTransitionTimeouts.push(timeout1);
            }
          });

          // Fade in target overlay
          const timeout2 = setTimeout(() => {
            targetOverlay.classList.remove('hidden');
            targetOverlay.style.opacity = '0';
            // Force reflow
            targetOverlay.offsetHeight;
            const timeout3 = setTimeout(() => {
              targetOverlay.style.opacity = '1';
            }, 10);
            this.overlayTransitionTimeouts.push(timeout3);
          }, 250);
          this.overlayTransitionTimeouts.push(timeout2);
        }
      }

      // Start underline animation - this represents the timer for the slideshow
      const underline = item.querySelector('.interactive-list-underline');
      if (underline) {
        // Reset to 0 first, ensure it's applied
        underline.style.transition = 'none';
        underline.style.width = '0';
        // Force reflow to ensure width: 0 is applied
        underline.offsetWidth;
        
        // Use requestAnimationFrame to ensure the reset is painted before starting animation
        requestAnimationFrame(() => {
          // Double-check we're still animating this item
          if (item.classList.contains('active') && underline) {
            // Set transition duration to match slide duration exactly
            const transitionValue = `width ${this.slideDuration}ms linear`;
            underline.style.transition = transitionValue;
            underline.style.width = '100%';
            
            // Start timer immediately after setting the animation
            // The CSS transition and setTimeout both start at the same moment
            if (startTimer) {
              this.startAutoPlay();
            }
          }
        });
      } else {
        // If no underline, start timer immediately
        if (startTimer) {
          this.startAutoPlay();
        }
      }

      this.currentIndex = index;
      this.isAnimating = false; // Allow next activation immediately
      this.lastActivationTime = Date.now(); // Track when we last activated
    }
  }

  // Initialize all interactive lists on page load
  function initInteractiveLists() {
    const sections = document.querySelectorAll('.interactive-list-section');
    
    sections.forEach((section) => {
      new InteractiveList(section);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInteractiveLists);
  } else {
    initInteractiveLists();
  }

  // Re-initialize on Shopify section load (for theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.detail.querySelector('.interactive-list-section');
      if (section) {
        new InteractiveList(section);
      }
    });
  }
})();
